import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ButtonComponent } from '@ppl/ui';
import { FocusTrapDirective } from '../../../../shared/directives/focus-trap.directive';
import {
  addProviderClassificationControls,
  deriveProviderType,
  ProviderClassificationQuestionsComponent,
} from '../../../../shared/components/provider-classification-questions/provider-classification-questions.component';
import { CheckListService } from '../../../../services/check-list.service';
import { NotificationService } from '../../../../shared/services/notification.service';
import { LoggerService } from '../../../../shared/services/logger.service';
import { DateTimeFormatterService } from '../../../../shared/utils/date-time-formatter.service';
import { getProviderClassificationForm } from '../../../../shared/constants/static-data.constants';

/**
 * Provider Classification on the provider detail page. The question flow itself lives in
 * ProviderClassificationQuestionsComponent; this wrapper reads the saved answers through
 * getPDFFormData and writes them back through the provider forms save endpoint.
 */
@Component({
  selector: 'ppl-provider-classification',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonComponent,
    FocusTrapDirective,
    ProviderClassificationQuestionsComponent,
  ],
  templateUrl: './provider-classification.component.html',
  styleUrl: './provider-classification.component.css',
})
export class ProviderClassificationComponent implements OnInit, OnChanges {
  @Input() pageId = '';
  @Input() tabId = '';
  @Input() readOnly = false;
  @Input() programCode = '';
  /** Internal provider id — the save endpoint's path parameter. */
  @Input() providerId: any;
  /** Provider PPL id — identifies the provider to getPDFFormData. */
  @Input() providerPplId = '';
  /** 'VE' for a vendor, 'IC' for an independent contractor, 'IF' for an independent facilitator. */
  @Input() userType = 'IC';

  @Output() leadUpdated = new EventEmitter<boolean>();

  @ViewChild('questions') questions?: ProviderClassificationQuestionsComponent;

  /**
   * Resolved on access rather than at construction. As a field initialiser this ran before
   * Angular had set @Input() userType, so it always picked the form for the default type.
   */
  private get classificationFormConfig() {
    return getProviderClassificationForm(this.userType);
  }

  get fields() {
    return this.classificationFormConfig.fields;
  }

  mode: 'read' | 'edit' = 'read';
  classificationForm!: FormGroup;
  isSaving = false;

  constructor(
    private fb: FormBuilder,
    private checkListService: CheckListService,
    private notificationService: NotificationService,
    private dateFormatter: DateTimeFormatterService,
    private logger: LoggerService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.classificationForm = this.fb.group({});
    addProviderClassificationControls(this.classificationForm, this.fb);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['programCode'] || changes['providerPplId']) && this.programCode && this.providerPplId) {
      this.loadClassification();
    }
  }

  loadClassification(): void {
    // getFormData builds its entity list from form.details unless overridden, so the
    // provider is supplied directly instead of going through a checklist item.
    const form: any = {
      formMasterId: this.classificationFormConfig.formMasterId,
      checklistItemId: this.classificationFormConfig.checklistItemId,
      checklistItemDetailId: this.classificationFormConfig.checklistItemDetailId,
      details: [],
    };

    this.checkListService
      .getFormData(this.programCode, form, 'provider', '', '', false, [
        { userType: this.userType, primaryId: this.providerPplId },
      ])
      .subscribe({
        next: (response: any) => {
          this.patchFormFields(response?.responsedata?.Form_Fields ?? []);
        },
        error: (err) => {
          this.logger.info('Error fetching provider classification', err);
        },
      });
  }

  private patchFormFields(formFields: any[]): void {
    const values = new Map<string, any>(formFields.map((field: any) => [field.fieldId, field.value]));

    this.classificationForm.patchValue(
      {
        servesAll: values.get(this.fields.servesAll) || null,
        vendored: values.get(this.fields.vendored) || null,
        followsProgramDesign: values.get(this.fields.followsProgramDesign) || null,
        vendorNumber: values.get(this.fields.vendorNumber) || null
      },
      // Patch silently — the branch-clearing subscriptions would wipe the loaded answers.
      { emitEvent: false }
    );

    this.questions?.applyBranchValidators();
    this.cdr.markForCheck();
  }

  onEdit(): void {
    this.mode = 'edit';
    this.cdr.markForCheck();
  }

  onCancel(): void {
    this.mode = 'read';
    this.loadClassification();
    this.questions?.resetAttestation();
    this.cdr.markForCheck();
  }

  onSave(): void {
    // Both are evaluated: an unconfirmed attestation has to show its own error alongside
    // whatever the answers are missing, not queue up behind them.
    const attested = this.questions?.confirmAttestation() ?? false;
    if (this.classificationForm.invalid || !attested) {
      this.classificationForm.markAllAsTouched();
      this.cdr.markForCheck();
      return;
    }

    const value = this.classificationForm.value;
    const formValue: Record<string, any> = {
      [this.fields.servesAll]: value.servesAll ?? '',
      [this.fields.vendored]: value.vendored ?? '',
      [this.fields.followsProgramDesign]: value.followsProgramDesign ?? '',
      [this.fields.vendorNumber]: value.vendorNumber ?? '',
      [this.fields.vendorClassification]: deriveProviderType(value),
    };

    const payload = {
      type: 'provider',
      checklistItems: [
        {
          checklistId: this.classificationFormConfig.checklistItemId,
          checklistDetailId: this.classificationFormConfig.checklistItemDetailId,
          participantId: '',
          providerId: Number(this.providerId),
          providerType: this.userType,
          associationId: [],
          formValue,
        },
      ],
    };

    this.isSaving = true;
    this.checkListService
      .saveParticipantProviderForms(this.programCode, this.providerId, false, payload)
      .subscribe({
        next: () => {
          this.isSaving = false;
          this.mode = 'read';
          this.loadClassification();
          this.questions?.resetAttestation();
          this.leadUpdated.emit(true);
          this.cdr.markForCheck();
        },
        error: (err) => {
          this.isSaving = false;
          this.logger.info('Error saving provider classification', err);
          this.notificationService.alert('error', 'Error', 'Failed to save Provider Classification');
          this.cdr.markForCheck();
        },
      });
  }
}
